import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calculator,
  Users,
  UserCheck,
  FileText,
  DollarSign,
  Search,
  Download,
  Send,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import EOSReportModal from './components/EOSReportModal';
import MarkAsPaidModal from './components/MarkAsPaidModal';
import OffboardingWorkflowModal from './components/OffboardingWorkflowModal';
import PayrollIntegrationModal from './components/PayrollIntegrationModal';
import { getCompanyUsers, CompanyUser } from '../../../api/TournamentManagement/refereeApi';
import {
  GetEosContext,
  PreviewEndOfService,
  ConfirmEndOfService,
  GetEndOfServiceTypes,
  PreviewSettlement,
} from '../../../api/Payroll/payroll';
import {
  EosContextView,
  EosBreakdownView,
  DepartureReasonEnum,
  EndOfServiceTypeView,
  SettlementBreakdownView,
  isForCauseArt44,
} from '../../../models/payroll';

// FDL 33/2021 departure reasons — grouped so HR can see when gratuity withholding applies (Art. 44).
type DepartureReasonGroup = {
  labelKey: string;
  defaultLabel: string;
  reasons: { value: DepartureReasonEnum; labelKey: string; defaultLabel: string }[];
};

const departureReasonGroups: DepartureReasonGroup[] = [
  {
    labelKey: 'eos.reasonGroup.employeeInitiated',
    defaultLabel: 'Employee-initiated',
    reasons: [
      { value: 'Resignation', labelKey: 'eos.reason.Resignation', defaultLabel: 'Resignation' },
    ],
  },
  {
    labelKey: 'eos.reasonGroup.employerWithoutCause',
    defaultLabel: 'Employer-initiated (without cause)',
    reasons: [
      { value: 'TerminationWithoutCause', labelKey: 'eos.reason.TerminationWithoutCause', defaultLabel: 'Termination without cause' },
      { value: 'Redundancy', labelKey: 'eos.reason.Redundancy', defaultLabel: 'Redundancy' },
      { value: 'MutualAgreement', labelKey: 'eos.reason.MutualAgreement', defaultLabel: 'Mutual agreement' },
    ],
  },
  {
    labelKey: 'eos.reasonGroup.art44',
    defaultLabel: 'Employer-initiated (for cause — Art. 44)',
    reasons: [
      { value: 'TerminationWithCause', labelKey: 'eos.reason.TerminationWithCause', defaultLabel: 'Termination with cause (generic)' },
      { value: 'TerminationArt44_Impersonation', labelKey: 'eos.reason.Art44_Impersonation', defaultLabel: 'Art. 44(1) — Impersonation / forged documents' },
      { value: 'TerminationArt44_FaultCausingLoss', labelKey: 'eos.reason.Art44_FaultCausingLoss', defaultLabel: 'Art. 44(2) — Fault causing material loss' },
      { value: 'TerminationArt44_SafetyViolation', labelKey: 'eos.reason.Art44_SafetyViolation', defaultLabel: 'Art. 44(3) — Gross safety violation' },
      { value: 'TerminationArt44_RepeatedMisconduct', labelKey: 'eos.reason.Art44_RepeatedMisconduct', defaultLabel: 'Art. 44(4) — Repeated disciplinary misconduct' },
      { value: 'TerminationArt44_AbsenceWithoutExcuse', labelKey: 'eos.reason.Art44_AbsenceWithoutExcuse', defaultLabel: 'Art. 44(5) — Unexcused absence (7 consecutive / 20 intermittent)' },
      { value: 'TerminationArt44_CompetitorEmployment', labelKey: 'eos.reason.Art44_CompetitorEmployment', defaultLabel: 'Art. 44(6) — Working for a competitor' },
      { value: 'TerminationArt44_TradeSecretBreach', labelKey: 'eos.reason.Art44_TradeSecretBreach', defaultLabel: 'Art. 44(7) — Trade secret disclosure' },
    ],
  },
  {
    labelKey: 'eos.reasonGroup.lifeEvents',
    defaultLabel: 'Life events',
    reasons: [
      { value: 'Retirement', labelKey: 'eos.reason.Retirement', defaultLabel: 'Retirement' },
      { value: 'DeathOrDisability', labelKey: 'eos.reason.DeathOrDisability', defaultLabel: 'Death or disability' },
    ],
  },
];

export default function EOSCenter() {
  const { t } = useTranslation();

  const [employees, setEmployees] = useState<CompanyUser[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [terminationType, setTerminationType] = useState<DepartureReasonEnum>('Resignation');
  // FDL 33/2021 Art. 44 — HR override: pay gratuity despite a for-cause reason.
  const [allowGratuityDespiteCause, setAllowGratuityDespiteCause] = useState<boolean>(false);
  const [contractType, setContractType] = useState<'Limited' | 'Unlimited'>('Unlimited');
  const [departureDate, setDepartureDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );

  const [eosTypes, setEosTypes] = useState<EndOfServiceTypeView[]>([]);

  const [context, setContext] = useState<EosContextView | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);

  const [breakdown, setBreakdown] = useState<EosBreakdownView | null>(null);
  const [settlement, setSettlement] = useState<SettlementBreakdownView | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [showOffboardingModal, setShowOffboardingModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);

  const getDisplayName = (emp: CompanyUser) =>
    emp.fullName?.trim() ||
    [emp.firstName, emp.lastName].filter(Boolean).join(' ').trim() ||
    emp.name?.trim() ||
    emp.email ||
    emp.id;

  const selectedEmployee = useMemo(
    () => employees.find(e => e.id === selectedUserId) ?? null,
    [employees, selectedUserId]
  );

  const extractErrorMessage = (e: unknown, fallback: string): string => {
    if (typeof e === 'string') return e;
    if (e instanceof Error) return e.message;
    const obj = e as { errors?: Array<{ description?: string; message?: string }>; message?: string };
    const first = obj?.errors?.[0];
    if (first) return first.description ?? first.message ?? fallback;
    if (typeof obj?.message === 'string') return obj.message;
    return fallback;
  };

  useEffect(() => {
    const loadInitial = async () => {
      setLoadingEmployees(true);
      try {
        const [usersRes, typesRes] = await Promise.all([
          getCompanyUsers(),
          GetEndOfServiceTypes(),
        ]);
        if (usersRes.errors.length === 0) setEmployees(usersRes.payload);
        else setError(usersRes.errors[0].description ?? t('common.error'));
        if (typesRes.errors.length === 0) setEosTypes(typesRes.payload);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingEmployees(false);
      }
    };
    loadInitial();
  }, [t]);

  const loadContext = useCallback(async (userId: string) => {
    setLoadingContext(true);
    setBreakdown(null);
    setSettlement(null);
    setError(null);
    try {
      const result = await GetEosContext(userId);
      if (result.errors.length === 0) setContext(result.payload);
      else {
        setContext(null);
        setError(result.errors[0].description ?? t('common.error'));
      }
    } catch (e) {
      setContext(null);
      setError(extractErrorMessage(e, t('common.error')));
    } finally {
      setLoadingContext(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedUserId) loadContext(selectedUserId);
    else setContext(null);
  }, [selectedUserId, loadContext]);

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      e =>
        getDisplayName(e).toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q)
    );
  }, [employees, searchTerm]);

  const buildLocalSettlement = (bd: EosBreakdownView): SettlementBreakdownView => {
    const daily = context ? context.basicSalary / 30 : 0;
    const leaveDays = Math.floor(context?.leaveBalance ?? 0);
    const leaveEncashment = daily * leaveDays;
    const daysInMonth = new Date(
      new Date(departureDate).getFullYear(),
      new Date(departureDate).getMonth() + 1,
      0
    ).getDate();
    const prorated = context
      ? (context.grossSalary / daysInMonth) * new Date(departureDate).getDate()
      : 0;
    const final = bd.finalGratuity + leaveEncashment + prorated;
    return {
      gratuityAmount: bd.finalGratuity,
      leaveEncashmentAmount: leaveEncashment,
      leaveDaysEncashed: leaveDays,
      proratedFinalMonthAmount: prorated,
      compensationInLieuAmount: 0,
      totalDeductions: 0,
      deductionCapApplied: false,
      deductionCapWarning: 0,
      finalSettlementAmount: final,
      lastWorkingDay: departureDate,
    };
  };

  const calculateEos = async () => {
    if (!selectedUserId || !context) return;
    if (eosTypes.length === 0) {
      setError(t('eos.noTypesConfigured', 'No EOS types configured. Configure them in Settings > Payroll > EOS first.'));
      return;
    }
    setCalculating(true);
    setError(null);
    setNotice(null);
    try {
      const eosTypeId = eosTypes[0].id;
      const previewRes = await PreviewEndOfService({
        userId: selectedUserId,
        endOfServiceId: eosTypeId,
        departureReason: terminationType,
        contractType,
        departureDate,
        allowGratuityDespiteCause,
      });
      if (previewRes.errors.length === 0) {
        setBreakdown(previewRes.payload);

        // Settlement preview requires a previously confirmed EOS record on the
        // backend. Only call it when one exists; otherwise compute an estimate
        // locally from the EOS breakdown + leave balance so the UI can still
        // surface the settlement picture before the user commits.
        if (context.existingEos) {
          try {
            const settlementRes = await PreviewSettlement({
              userId: selectedUserId,
              noticePeriod: 30,
              compensationInLieu: false,
              lastWorkingDay: departureDate,
              totalDeductions: 0,
            });
            if (settlementRes.errors.length === 0) {
              setSettlement(settlementRes.payload);
            } else {
              setSettlement(buildLocalSettlement(previewRes.payload));
            }
          } catch {
            setSettlement(buildLocalSettlement(previewRes.payload));
          }
        } else {
          setSettlement(buildLocalSettlement(previewRes.payload));
          setNotice(
            t(
              'eos.confirmToUnlockSettlement',
              'Confirm & Save the EOS to unlock an authoritative settlement preview.'
            )
          );
        }
      } else {
        setError(previewRes.errors[0].description ?? t('common.error'));
      }
    } catch (e) {
      setError(extractErrorMessage(e, t('common.error')));
    } finally {
      setCalculating(false);
    }
  };

  const confirmEos = async () => {
    if (!selectedUserId || !breakdown || eosTypes.length === 0) return;
    setConfirming(true);
    setError(null);
    try {
      const result = await ConfirmEndOfService({
        userId: selectedUserId,
        endOfServiceId: eosTypes[0].id,
        departureReason: terminationType,
        contractType,
        departureDate,
        allowGratuityDespiteCause,
      });
      if (result.errors.length === 0) {
        setNotice(t('eos.confirmed', 'End of service confirmed and saved.'));
        if (selectedUserId) await loadContext(selectedUserId);
      } else {
        setError(result.errors[0].description ?? t('common.error'));
      }
    } catch (e) {
      setError(extractErrorMessage(e, t('common.error')));
    } finally {
      setConfirming(false);
    }
  };

  const totalEos =
    (breakdown?.finalGratuity ?? 0) +
    (settlement?.leaveEncashmentAmount ?? 0) +
    (settlement?.proratedFinalMonthAmount ?? 0) +
    (settlement?.compensationInLieuAmount ?? 0) -
    (settlement?.totalDeductions ?? 0);

  const employeeSummary = selectedEmployee
    ? {
        id: selectedEmployee.id,
        name: getDisplayName(selectedEmployee),
        employeeId: selectedEmployee.id,
        department: '-',
        position: '-',
      }
    : null;

  const calcSummary = {
    totalAmount: totalEos,
    gratuity: { adjustedAmount: breakdown?.finalGratuity ?? 0 },
    leaveEncashment: settlement?.leaveEncashmentAmount ?? 0,
    pendingDeductions: settlement?.totalDeductions ?? 0,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-xl flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('EOS Center')}</h1>
              <p className="text-gray-600 mt-1">{t('End of Service calculation and management')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>{t('EOS Calculator Ready')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 text-sm font-medium">{t('Total Employees')}</p>
                <p className="text-2xl font-bold text-blue-900">{employees.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-700 text-sm font-medium">{t('Selected Employee')}</p>
                <p className="text-lg font-semibold text-amber-900 truncate">
                  {selectedEmployee ? getDisplayName(selectedEmployee) : t('None Selected')}
                </p>
              </div>
              <UserCheck className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-700 text-sm font-medium">{t('Termination Type')}</p>
                <p className="text-lg font-semibold text-emerald-900">
                  {t(`eos.reason.${terminationType}`, terminationType)}
                </p>
                {isForCauseArt44(terminationType) && (
                  <p className="text-[10px] text-amber-700 mt-0.5">
                    {t('eos.art44.badge', 'Art. 44 — gratuity may be withheld')}
                  </p>
                )}
              </div>
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-700 text-sm font-medium">{t('Total EOS Amount')}</p>
                <p className="text-xl font-bold text-purple-900">
                  {breakdown
                    ? `AED ${totalEos.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : t('Not Calculated')}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        <div className="xl:col-span-3">
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Select Employee')}</h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('Search employees...')}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            {loadingEmployees ? (
              <div className="py-8 flex items-center justify-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('Loading...')}
              </div>
            ) : filteredEmployees.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('No employees found')}
              </p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredEmployees.map(emp => (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => setSelectedUserId(emp.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedUserId === emp.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-border hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium text-gray-900 text-sm">{getDisplayName(emp)}</p>
                    {emp.email && (
                      <p className="text-xs text-gray-500 truncate">{emp.email}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-6">
          <div className="space-y-6">
            <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('Termination Configuration')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Termination Type')}
                  </label>
                  <select
                    value={terminationType}
                    onChange={e => {
                      const next = e.target.value as DepartureReasonEnum;
                      setTerminationType(next);
                      if (!isForCauseArt44(next)) setAllowGratuityDespiteCause(false);
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  >
                    {departureReasonGroups.map(group => (
                      <optgroup key={group.labelKey} label={t(group.labelKey, group.defaultLabel)}>
                        {group.reasons.map(r => (
                          <option key={r.value} value={r.value}>
                            {t(r.labelKey, r.defaultLabel)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Contract Type')}
                  </label>
                  <select
                    value={contractType}
                    onChange={e => setContractType(e.target.value as 'Limited' | 'Unlimited')}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  >
                    <option value="Unlimited">{t('Unlimited')}</option>
                    <option value="Limited">{t('Limited')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Departure Date')}
                  </label>
                  <input
                    type="date"
                    value={departureDate}
                    onChange={e => setDepartureDate(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  />
                </div>
              </div>

              {isForCauseArt44(terminationType) && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600" />
                    <div className="flex-1">
                      <p className="font-medium">
                        {t('eos.art44.title', 'Art. 44 — For-cause termination')}
                      </p>
                      <p className="mt-1">
                        {t(
                          'eos.art44.warning',
                          'Art. 44 permits gratuity withholding. Review the specific sub-article and consult HR policy before finalizing.'
                        )}
                      </p>
                      <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allowGratuityDespiteCause}
                          onChange={e => setAllowGratuityDespiteCause(e.target.checked)}
                          className="rounded border-amber-400 text-amber-600 focus:ring-amber-400"
                        />
                        <span>
                          {t('eos.art44.override', 'Allow gratuity despite cause (HR override)')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('EOS Calculation Summary')}
              </h3>
              {!selectedEmployee ? (
                <div className="text-center py-8">
                  <Calculator className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    {t('Select an employee to view EOS calculation')}
                  </p>
                </div>
              ) : loadingContext ? (
                <div className="py-8 flex items-center justify-center text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('Loading context...')}
                </div>
              ) : !context ? (
                <p className="text-sm text-muted-foreground">
                  {t('No context available for this employee.')}
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium">{t('Basic Salary')}</p>
                      <p className="text-lg font-bold text-blue-900">
                        AED {context.basicSalary.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium">{t('Daily Rate')}</p>
                      <p className="text-lg font-bold text-blue-900">
                        AED {(context.basicSalary / 30).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium">{t('Service Years')}</p>
                      <p className="text-lg font-bold text-blue-900">
                        {context.serviceYearsDecimal.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium">{t('Law Version')}</p>
                      <p className="text-lg font-bold text-blue-900">{context.lawVersion}</p>
                    </div>
                  </div>

                  {!context.isEligible && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5" />
                      <div>
                        <p className="font-medium">
                          {t('Not yet eligible for EOS (min 1 year required).')}
                        </p>
                        {context.eligibleFrom && (
                          <p className="text-xs mt-0.5">
                            {t('Eligible from')}:{' '}
                            {new Date(context.eligibleFrom).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {breakdown ? (
                    <div className="border-t border-border pt-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-emerald-50 rounded-lg">
                          <p className="text-xs text-emerald-700 font-medium">
                            {t('Final Gratuity')}
                          </p>
                          <p className="text-xl font-bold text-emerald-900">
                            AED {breakdown.finalGratuity.toFixed(2)}
                          </p>
                          {breakdown.isCapped && (
                            <p className="text-xs text-emerald-600 mt-0.5">
                              {t('Capped at 2-year salary')}
                            </p>
                          )}
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-700 font-medium">
                            {t('Leave Encashment')}
                          </p>
                          <p className="text-xl font-bold text-purple-900">
                            AED {(settlement?.leaveEncashmentAmount ?? 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-purple-600 mt-0.5">
                            {settlement?.leaveDaysEncashed ?? 0} {t('days')}
                          </p>
                        </div>
                      </div>

                      {breakdown.complianceFlags.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-700">
                            {t('Compliance Checks')}
                          </p>
                          {breakdown.complianceFlags.map((flag, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2 text-xs py-1 border-b border-border last:border-0"
                            >
                              {flag.passed ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                              ) : (
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                              )}
                              <span className="flex-1">{flag.label}</span>
                              <span className="text-gray-500">{flag.detail}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-t border-border pt-4 text-center text-sm text-gray-500">
                      {t('Click "Calculate EOS" to preview the breakdown.')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="xl:col-span-3">
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Actions')}</h3>
            <div className="space-y-3">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={calculateEos}
                disabled={!selectedEmployee || calculating || !context?.isEligible}
              >
                {calculating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4" />
                )}
                {t('Calculate EOS')}
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-lg hover:bg-indigo-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={confirmEos}
                disabled={!breakdown || confirming}
              >
                {confirming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {t('Confirm & Save')}
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-lg hover:bg-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => selectedEmployee && setShowOffboardingModal(true)}
                disabled={!selectedEmployee}
              >
                <ClipboardList className="w-4 h-4" />
                {t('Start Offboarding')}
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 bg-amber-100 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => selectedEmployee && setShowPayrollModal(true)}
                disabled={!selectedEmployee || !breakdown}
              >
                <Send className="w-4 h-4" />
                {t('Add to Payroll')}
              </button>

              <div className="border-t border-border pt-3 mt-3">
                <p className="text-xs text-gray-500 font-medium mb-3">{t('Settlement Actions')}</p>
                <div className="space-y-2">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-purple-100 border border-purple-300 text-purple-800 rounded-lg text-sm hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => selectedEmployee && setShowMarkAsPaidModal(true)}
                    disabled={!selectedEmployee || !breakdown}
                  >
                    <DollarSign className="w-4 h-4" />
                    {t('Mark as Paid')}
                  </button>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-lg text-sm hover:bg-indigo-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => selectedEmployee && setShowReportModal(true)}
                    disabled={!selectedEmployee || !breakdown}
                  >
                    <FileText className="w-4 h-4" />
                    {t('Generate Report')}
                  </button>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-100 border border-gray-300 text-gray-800 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!selectedEmployee || !breakdown}
                  >
                    <Download className="w-4 h-4" />
                    {t('Export EOS Data')}
                  </button>
                </div>
              </div>
            </div>

            {selectedEmployee && context && (
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('Employee Details')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('Name')}</span>
                    <span className="text-gray-900 font-medium">{getDisplayName(selectedEmployee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('Hire Date')}</span>
                    <span className="text-gray-900">
                      {new Date(context.hireDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('Service')}</span>
                    <span className="text-gray-900">
                      {context.serviceYearsDecimal.toFixed(2)}y
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('Basic Salary')}</span>
                    <span className="text-gray-900 font-medium">
                      AED {context.basicSalary.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('Leave Balance')}</span>
                    <span className="text-gray-900">{context.leaveBalance}</span>
                  </div>
                  {context.settlementStatus && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('Settlement')}</span>
                      <span className="text-gray-900">{context.settlementStatus}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {employeeSummary && (
        <>
          <EOSReportModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            employee={employeeSummary}
            calculation={calcSummary}
            terminationType={terminationType}
            onReportGenerated={() => setShowReportModal(false)}
          />
          <MarkAsPaidModal
            isOpen={showMarkAsPaidModal}
            onClose={() => setShowMarkAsPaidModal(false)}
            employee={employeeSummary}
            calculation={{ totalAmount: totalEos }}
            onPaymentRecorded={() => setShowMarkAsPaidModal(false)}
          />
          <OffboardingWorkflowModal
            isOpen={showOffboardingModal}
            onClose={() => setShowOffboardingModal(false)}
            employee={employeeSummary}
            eosStatus={{ isPaid: context?.settlementStatus === 'Paid' }}
            onOffboardingStarted={() => setShowOffboardingModal(false)}
          />
          <PayrollIntegrationModal
            isOpen={showPayrollModal}
            onClose={() => setShowPayrollModal(false)}
            employee={employeeSummary}
            calculation={calcSummary}
            onPayrollAdded={() => setShowPayrollModal(false)}
          />
        </>
      )}
    </div>
  );
}
